import dayjs from "dayjs";
import * as units from "./units";
import * as unitEvaluations from "./unitEvaluations";
import * as assets from "./assets";
import * as landtracUnits from "./landtracUnits";
import * as landtracLeases from "./landtracLeases";
import * as permitPlats from "./permitPlats";
import * as wells from "./wells";
import * as tracts from "./tracts";
import * as titleTracts from "./titleTracts";
import * as titleAssets from "./titleAssets";
import * as areaOfInterests from "./areaOfInterests";
import * as rigs from "./rigs";
import * as enverusLeases from "./enverusLeases";
import * as plays from "./plays";
import * as basins from "./basins";
import * as faults from "./faults";
import * as landGrid from "./landGrid";
import {
  DTYPES,
  DATE_FILTER_TYPES,
  DATE_FILTER_SINCE_UNITS,
} from "./constants";

// https://colorbrewer2.org/#type=qualitative&scheme=Dark2&n=6
// #4bbf90
// #ff5f23
// #721ea9
// #FE00FE
// #5FB300
// #f0ad4e
// #317ae2
// #00BCD4
// #D20C0C
// #FFFFFF
// #7f888f

const layerModules = {
  [wells.LAYER_ID]: wells,
  [landtracUnits.LAYER_ID]: landtracUnits,
  [landtracLeases.LAYER_ID]: landtracLeases,
  [permitPlats.LAYER_ID]: permitPlats,
  [units.LAYER_ID]: units,
  [unitEvaluations.LAYER_ID]: unitEvaluations,
  [assets.LAYER_ID]: assets,
  [tracts.LAYER_ID]: tracts,
  [titleTracts.LAYER_ID]: titleTracts,
  [titleAssets.LAYER_ID]: titleAssets,
  [areaOfInterests.LAYER_ID]: areaOfInterests,
  [rigs.LAYER_ID]: rigs,
  [enverusLeases.LAYER_ID]: enverusLeases,
  // Reference layers anchor before the same wells layer, so registration
  // order sets their stacking: plays fills lowest, faults lines on top.
  [plays.LAYER_ID]: plays,
  [basins.LAYER_ID]: basins,
  [faults.LAYER_ID]: faults,
  [landGrid.LAYER_ID]: landGrid,
};

const SOURCE_IDS = [
  units.SOURCE_ID,
  unitEvaluations.SOURCE_ID,
  assets.SOURCE_ID,
  landtracUnits.SOURCE_ID,
  landtracLeases.SOURCE_ID,
  permitPlats.SOURCE_ID,
  tracts.SOURCE_ID,
  areaOfInterests.SOURCE_ID,
  rigs.SOURCE_ID,
  enverusLeases.SOURCE_ID,
];

const DRAW_SNAP_LAYER_IDS = [
  units.LAYER_ID,
  assets.LAYER_ID,
  landtracUnits.LAYER_ID,
  landtracLeases.LAYER_ID,
  permitPlats.LAYER_ID,
  tracts.LAYER_ID,
  areaOfInterests.LAYER_ID,
  unitEvaluations.LAYER_ID,
  "Enverus grid sections",
];

const CLICKABLE_STORAGE_KEY = "mapLayerClickable";

/* Whether the user opted into clicking a style layer. Persisted across reloads. */
const loadLayerClickable = (layerId) => {
  try {
    const stored = JSON.parse(
      window.localStorage.getItem(CLICKABLE_STORAGE_KEY)
    );
    return stored?.[layerId] === true;
  } catch (_err) {
    return false;
  }
};

const saveLayerClickable = (layerId, value) => {
  try {
    const stored =
      JSON.parse(window.localStorage.getItem(CLICKABLE_STORAGE_KEY)) || {};
    window.localStorage.setItem(
      CLICKABLE_STORAGE_KEY,
      JSON.stringify({ ...stored, [layerId]: value })
    );
  } catch (_err) {
    // Private browsing or a full quota — the choice just won't persist.
  }
};

/* LAYER_ID of the module that owns a clickable map layer, else the id itself. */
const getModuleIdForLayer = (layerId) =>
  Object.values(layerModules).find((module) =>
    module.CLICKABLE_LAYERS?.includes(layerId)
  )?.LAYER_ID || layerId;

const addAll = (mapRef) => {
  const map = mapRef.current;

  Object.values(layerModules).forEach((module) => {
    if (module.addAll) {
      module.addAll(mapRef);
    }

    setHighlightVisibility(map, module, "none");
  });
};

// Every highlight layer a module exposes. Most modules have a single
// BORDER_HIGHLIGHT_LAYER_ID; some (e.g. wells) highlight several layers at once
// via HIGHLIGHT_LAYER_IDS (bottom-hole point + lateral line).
const highlightLayerIdsFor = (module) =>
  (
    module.HIGHLIGHT_LAYER_IDS ||
    (module.BORDER_HIGHLIGHT_LAYER_ID ? [module.BORDER_HIGHLIGHT_LAYER_ID] : [])
  ).filter(Boolean);

const setHighlightVisibility = (map, module, visibility) => {
  highlightLayerIdsFor(module).forEach((id) => {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, "visibility", visibility);
    }
  });
};

const setHighlightFilters = (mapRef, layerId, id) => {
  if (!mapRef || !mapRef.current) return;

  removeHighlightFilters(mapRef);

  const module = layerModules[layerId];

  if (module && module.UNIQUE_COLUMN) {
    highlightLayerIdsFor(module).forEach((highlightLayerId) => {
      if (mapRef.current.getLayer(highlightLayerId)) {
        mapRef.current.setFilter(highlightLayerId, [
          "==",
          module.UNIQUE_COLUMN,
          id,
        ]);
      }
    });

    setHighlightVisibility(mapRef.current, module, "visible");
  }
};

// For highlights that need their own filter (e.g. table hover, several wells
// at once). Highlight layers are hidden by default, so visibility has to follow
// the filter — a bare map.setFilter draws nothing. Null expression clears.
const setHighlightFilterExpression = (mapRef, layerId, filterExpression) => {
  if (!mapRef || !mapRef.current) return;

  const module = layerModules[layerId];

  if (!module || !module.UNIQUE_COLUMN) return;

  highlightLayerIdsFor(module).forEach((highlightLayerId) => {
    if (mapRef.current.getLayer(highlightLayerId)) {
      mapRef.current.setFilter(
        highlightLayerId,
        filterExpression || ["in", module.UNIQUE_COLUMN, ""]
      );
    }
  });

  setHighlightVisibility(
    mapRef.current,
    module,
    filterExpression ? "visible" : "none"
  );
};

const removeHighlightFilters = (mapRef) => {
  if (!mapRef || !mapRef.current) return;

  Object.values(layerModules).forEach((module) => {
    if (!module.UNIQUE_COLUMN) return;

    highlightLayerIdsFor(module).forEach((highlightLayerId) => {
      if (mapRef.current.getLayer(highlightLayerId)) {
        mapRef.current.setFilter(highlightLayerId, [
          "in",
          module.UNIQUE_COLUMN,
          "",
        ]);
      }
    });

    setHighlightVisibility(mapRef.current, module, "none");
  });
};

// TODO - Generalize - move setAnnotationVisibility to layer module
const setAnnotationVisibility = (action) => {
  const { layerId } = action;
  const module = layerModules[layerId];
  if (module && module.setAnnotationVisibility) {
    module.setAnnotationVisibility(action);
  }
};

const setLayerVisibility = (action, styles = null) => {
  const { layerId, mapRef, value } = action;
  if (!mapRef || !mapRef.current) return;

  const module = layerModules[layerId];
  if (!module || !module.FILTER_LAYER_IDS) return;

  const map = mapRef.current;
  const visibility = value ? "visible" : "none";

  module.FILTER_LAYER_IDS.forEach((id) => {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, "visibility", visibility);
    }
  });

  // Don't enable annotation visibility if the layer is not visible
  if (
    module &&
    module.LAYER_ANNOTATION_ID &&
    styles &&
    map.getLayer(module.LAYER_ANNOTATION_ID)
  ) {
    const annotationVisible = styles[module.LAYER_ID].annotationVisible;
    map.setLayoutProperty(
      module.LAYER_ANNOTATION_ID,
      "visibility",
      annotationVisible && visibility === "visible" ? "visible" : "none"
    );
  }
};

const generateBooleanFilter = (
  column,
  value,
  excludeSelected,
  excludeNullValues
) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (excludeSelected) {
    // Exclude selected values, but exclude NULL
    if (excludeNullValues) {
      return ["!=", column, !!value];
    }

    // Exclude selected values, but include NULL
    return ["any", ["!has", column], ["!=", column, !!value]];
  }

  // Include only selected values
  return ["==", column, !!value];
};

const generateDateExplicitFilter = (
  column,
  value,
  excludeSelected,
  excludeNullValues
) => {
  if (excludeSelected) {
    const orConditions = ["any"];

    // Exclude selected values, but include NULL
    if (!excludeNullValues) {
      orConditions.push(["!has", column]);
    }

    if (value.start_date) {
      orConditions.push(["<", column, value.start_date]);
    }
    if (value.end_date) {
      orConditions.push([">", column, value.end_date]);
    }

    return orConditions;
  }

  const allConditions = ["all"];

  if (value.start_date) {
    allConditions.push([">=", column, value.start_date]);
  }

  if (value.end_date) {
    allConditions.push(["<=", column, value.end_date]);
  }

  return allConditions;
};

const generateDateImplicitFilter = (
  column,
  value,
  excludeSelected,
  excludeNullValues
) => {
  const endOfDay = dayjs().endOf("day");

  let startDate = dayjs();
  const unitMap = {
    [DATE_FILTER_SINCE_UNITS.DAYS]: "days",
    [DATE_FILTER_SINCE_UNITS.WEEKS]: "weeks",
    [DATE_FILTER_SINCE_UNITS.MONTHS]: "months",
    [DATE_FILTER_SINCE_UNITS.YEARS]: "years",
  };

  const unit = unitMap[value.sinceUnit];

  if (unit) {
    startDate = startDate.subtract(value.sinceValue, unit);
  }

  const startTimestamp = startDate.unix();
  const endTimestamp = endOfDay.unix();

  if (excludeSelected) {
    const orConditions = ["any"];

    // Exclude selected values, but include NULL
    if (!excludeNullValues) {
      orConditions.push(["!has", column]);
    }

    orConditions.push(["<", column, startTimestamp]);
    orConditions.push([">", column, endTimestamp]);

    return orConditions;
  }

  return ["all", [">=", column, startTimestamp], ["<=", column, endTimestamp]];
};

const generateStringFilter = (
  column,
  value,
  excludeSelected,
  excludeNullValues
) => {
  const values = value.map((item) => item.value);

  if (excludeSelected) {
    // Exclude selected values and exclude NULL
    if (excludeNullValues) {
      return ["!in", column, ...values];
    }

    // Exclude selected values, but include NULL
    return ["any", ["!has", column], ["!in", column, ...values]];
  }

  // Include only selected values
  return ["in", column, ...values];
};

const generateColumnFilterExpression = (
  column,
  value,
  dtype = DTYPES.STRING,
  excludeSelected = false,
  excludeNullValues = false
) => {
  if (!column || (dtype !== DTYPES.BOOLEAN && !value)) {
    return null;
  }

  switch (dtype) {
    case DTYPES.BOOLEAN:
      return generateBooleanFilter(
        column,
        value,
        excludeSelected,
        excludeNullValues
      );

    case DTYPES.DATE:
      if (
        value.type === DATE_FILTER_TYPES.EXPLICIT &&
        (value.start_date || value.end_date)
      ) {
        return generateDateExplicitFilter(
          column,
          value,
          excludeSelected,
          excludeNullValues
        );
      }
      if (
        value.type === DATE_FILTER_TYPES.IMPLICIT &&
        value.sinceType &&
        value.sinceValue &&
        value.sinceUnit
      ) {
        return generateDateImplicitFilter(
          column,
          value,
          excludeSelected,
          excludeNullValues
        );
      }
      return null;

    // Integers filter the same way strings do -- a list of values to keep or
    // drop. They differ only server-side, where the comparison has to skip the
    // ::TEXT cast to stay on the column's index.
    case DTYPES.STRING:
    case DTYPES.INTEGER:
      if (value.length > 0) {
        return generateStringFilter(
          column,
          value,
          excludeSelected,
          excludeNullValues
        );
      }
      return null;

    default:
      return null;
  }
};

const buildCombinedFilterExpression = (filters) => {
  const expressions = Object.entries(filters || {})
    .map(([column, filter]) =>
      generateColumnFilterExpression(
        column,
        filter.value,
        filter.dtype,
        filter.excludeSelected,
        filter.excludeNullValues
      )
    )
    .filter(Boolean);

  if (expressions.length === 0) return null;
  if (expressions.length === 1) return expressions[0];

  return ["all", ...expressions];
};

const setLayerFilterExpression = (action) => {
  const {
    layerId,
    mapRef,
    column,
    value = [],
    filterExpression,
    dtype = DTYPES.STRING,
    excludeSelected = false,
    excludeNullValues = false,
  } = action;
  if (!mapRef || !mapRef.current) return;

  const module = layerModules[layerId];
  if (!module || !module.FILTER_LAYER_IDS) return;

  const map = mapRef.current;

  let _filterExpression = null;
  if (filterExpression) {
    _filterExpression = filterExpression;
  } else {
    _filterExpression = generateColumnFilterExpression(
      column,
      value,
      dtype,
      excludeSelected,
      excludeNullValues
    );
  }

  module.FILTER_LAYER_IDS.forEach((id) => {
    if (!map.getLayer(id)) return;

    const baseFilter = module.LAYER_BASE_FILTERS?.[id];
    const combined = combineFilters(baseFilter, _filterExpression);
    map.setFilter(id, combined);
  });

  // Also filter annotations
  if (
    module &&
    module.LAYER_ANNOTATION_ID &&
    map.getLayer(module.LAYER_ANNOTATION_ID)
  ) {
    const baseFilter = module.LAYER_BASE_FILTERS?.[module.LAYER_ANNOTATION_ID];
    const combined = combineFilters(baseFilter, _filterExpression);
    map.setFilter(module.LAYER_ANNOTATION_ID, combined);
  }
};

const combineFilters = (baseFilter, userFilter) => {
  if (!baseFilter) return userFilter;
  if (!userFilter) return baseFilter;

  return ["all", baseFilter, userFilter];
};

const resetMap = (action, initialState) => {
  const { mapRef } = action;

  if (!mapRef || !mapRef.current) return;

  Object.values(layerModules).forEach((module) => {
    const layerId = module.LAYER_ID;
    const layerState = initialState.layers[layerId];

    if (!layerState) {
      if (module.setAnnotationVisibility && initialState.styles[layerId]) {
        module.setAnnotationVisibility({
          mapRef,
          layerId,
          value: initialState.styles[layerId].annotationVisible,
        });
      }
      return;
    }

    const { layerVisible, filters } = layerState;

    setLayerVisibility({ mapRef, layerId, value: layerVisible });
    setLayerFilterExpression({
      mapRef,
      layerId,
      filterExpression: buildCombinedFilterExpression(filters),
    });
    removeHighlightFilters(mapRef, layerId);
  });
};

export {
  layerModules,
  addAll,
  SOURCE_IDS,
  DRAW_SNAP_LAYER_IDS,
  DTYPES,
  removeHighlightFilters,
  setHighlightFilters,
  setHighlightFilterExpression,
  setAnnotationVisibility,
  setLayerVisibility,
  generateColumnFilterExpression,
  buildCombinedFilterExpression,
  setLayerFilterExpression,
  resetMap,
  loadLayerClickable,
  saveLayerClickable,
  getModuleIdForLayer,
};
